import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const packageJson = require('../../package.json');

/**
 * Sentry DSN for the Otuvy Frontline project.
 * Org: o4511106696019968 (Otuvy, same as otuvyQM)
 * Project: 4511419056586752 (slug: otuvy-frontline)
 *
 * Safe to commit — Sentry DSNs are write-only event ingest keys, not auth secrets.
 * otuvyQM follows the same hardcode-in-source pattern with its own project DSN.
 *
 * To disable Sentry locally for debugging, set this constant to an empty string
 * and initSentry() will skip initialization with a console.warn.
 */
const SENTRY_DSN: string =
  'https://b65c6ab97fc2e87040b09ca2a5f76d6e@o4511106696019968.ingest.us.sentry.io/4511419056586752';

const appVersion: string = packageJson.version;
const isDev = process.env.NODE_ENV !== 'production';

/**
 * Initialize Sentry crash + error reporting.
 *
 * Wraps @sentry/capacitor (native iOS + Android crash capture) with @sentry/react
 * (browser/React error capture). On native platforms, errors caught here include
 * unhandled native exceptions; on web, only JS-side errors are captured.
 *
 * Should be called once, before React renders, from src/index.tsx. The async work
 * (CapApp.getInfo) is best-effort; if it throws, init continues without those tags.
 *
 * Sentry init failure must NEVER block app bootstrap; the outer try/catch enforces this.
 */
export async function initSentry(): Promise<void> {
  if (!SENTRY_DSN) {
    console.warn('[sentry] DSN not configured; skipping init');
    return;
  }

  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

  let dist: string | undefined;
  let nativeVersion: string | undefined;
  let nativeBuild: string | undefined;
  let appId: string | undefined;

  if (Capacitor.isNativePlatform()) {
    try {
      const info = await CapApp.getInfo();
      dist = info.build;
      nativeVersion = info.version;
      nativeBuild = info.build;
      appId = info.id;
    } catch {
      // Native bridge unavailable -> fall through with undefined values.
    }
  }

  try {
    Sentry.init(
      {
        dsn: SENTRY_DSN,

        release: `otuvy-frontline@${appVersion}`,
        dist,
        environment: process.env.REACT_APP_SENTRY_ENV || (isDev ? 'development' : 'production'),

        // Default integrations from @sentry/capacitor + @sentry/browser are used.
        // We deliberately do NOT pass @sentry/react-specific integrations (e.g.
        // httpClientIntegration, extraErrorDataIntegration) here because they bring
        // a transitive @sentry/core whose Integration type does not unify with the
        // one @sentry/capacitor expects (npm hoists two @sentry/core copies). Those
        // integrations can be added later by importing from @sentry/browser (which
        // @sentry/capacitor re-exports) or by aligning the two SDK versions.

        // 100% in dev for debugging; 30% in production for cost efficiency.
        tracesSampleRate: isDev ? 1.0 : 0.3,

        // Sentry Logs - structured, trace-connected, queryable via Sentry.logger.*
        enableLogs: true,

        // Distributed tracing disabled until backend CORS allows sentry-trace + baggage
        // headers. Same disposition otuvyQM uses.
        tracePropagationTargets: [],

        // Filter common browser noise that isn't actionable.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          /^Network request failed$/,
          'Load failed',
          'Failed to fetch',
        ],

        sendDefaultPii: false,

        // Raised from the SDK default of 100. The CC-1864 sync instrumentation
        // (setup/sentryLogging.ts logSyncPhase) emits 3 breadcrumbs per sync cycle, and
        // syncIntervalInMs is 5s on master / 10s on release — i.e. ~36/min or ~18/min. At
        // the default the whole buffer is sync noise within ~3 minutes, evicting the
        // console/HTTP/navigation breadcrumbs that every OTHER issue in the app relies on.
        // 200 restores a useful trail without a payload-size risk (Sentry drops events that
        // exceed the max payload size, so this is deliberately moderate rather than large).
        // If crowding is still a problem, the next lever is emitting fewer sync phases
        // rather than raising this further.
        maxBreadcrumbs: 200,

        initialScope: {
          tags: {
            platform,
            ...(appId && { app_id: appId }),
            ...(nativeVersion && { native_version: nativeVersion }),
            ...(nativeBuild && { native_build: nativeBuild }),
          },
        },
      },
      SentryReact.init
    );
  } catch (e) {
    // Never let Sentry init failure block app bootstrap.
    console.error('[sentry] init failed', e);
  }
}
